import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Select, MenuItem, FormControlLabel, Checkbox, Divider, Button, Chip, IconButton } from '@mui/material';
import {
  Phone as PhoneIcon,
  Tune as TuneIcon,
  Storage as DicomIcon,
  ViewColumn as ColumnsIcon,
  Tab as TabsIcon,
  Monitor as MonitorIcon,
  Settings as SettingsIcon,
  Keyboard as KeyboardIcon,
  TableRows as RowsIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
} from '@mui/icons-material';
import { WizardDialog, type WizardPanelDef } from '../../shared/components/WizardDialog';
import { useQueryClient } from '@tanstack/react-query';
import { gwtStyles } from '../../config/theme';
import {
  HOT_KEY_ACTIONS,
  formatKeyCombination,
  loadUserBindings,
  saveUserBindings,
  getDefaultBindings,
  type KeysCombination,
  type HotKeyCategory,
  type HotKeyBindings,
} from '../hotkeys/hotkeys';
import { usePreferences, type UserPreferences } from './usePreferences';
import { useAuth } from '../auth/useAuth';
import { api } from '../../api/rpc';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface UserPreferencesDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * User Preferences dialog — mirrors WizardDialog with WizardPanels enum.
 *
 * 9 panels:
 *   1. Contact — Personal Contact Information
 *   2. UserCustomization — Notification Configuration
 *   3. Dicom — DICOM Information
 *   4. Details — Details Layout
 *   5. Column — Columns Layout
 *   6. WorkListTab — Tabs
 *   7. MonitorConfiguration — Monitor Configuration
 *   8. Application — Application Settings
 *   9. HotKeys — HotKeys
 */
export const UserPreferencesDialog = ({ open, onClose }: UserPreferencesDialogProps) => {
  const { prefs, updatePref, save, saving } = usePreferences(open);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const panels = useMemo<WizardPanelDef[]>(
    () => [
      {
        id: 'contact',
        label: 'Contact',
        icon: <PhoneIcon sx={{ fontSize: 18 }} />,
        content: <ContactPanel prefs={prefs} onChange={updatePref} />,
      },
      {
        id: 'notifications',
        label: 'Notifications',
        icon: <TuneIcon sx={{ fontSize: 18 }} />,
        content: <NotificationsPanel prefs={prefs} onChange={updatePref} />,
      },
      {
        id: 'dicom',
        label: 'DICOM',
        icon: <DicomIcon sx={{ fontSize: 18 }} />,
        content: <DicomPanel dicomAe={user?.dicomAe} dicomHost={user?.dicomHost} dicomPort={user?.dicomPort} />,
      },
      {
        id: 'details',
        label: 'Details Layout',
        icon: <RowsIcon sx={{ fontSize: 18 }} />,
        content: <DetailsLayoutPanel prefs={prefs} onChange={updatePref} />,
      },
      {
        id: 'columns',
        label: 'Columns',
        icon: <ColumnsIcon sx={{ fontSize: 18 }} />,
        content: <ColumnsLayoutPanel prefs={prefs} onChange={updatePref} />,
      },
      {
        id: 'tabs',
        label: 'Tabs',
        icon: <TabsIcon sx={{ fontSize: 18 }} />,
        content: <TabsPanel prefs={prefs} onChange={updatePref} />,
      },
      {
        id: 'monitor',
        label: 'Monitors',
        icon: <MonitorIcon sx={{ fontSize: 18 }} />,
        content: <MonitorPanel prefs={prefs} onChange={updatePref} />,
      },
      {
        id: 'application',
        label: 'Application',
        icon: <SettingsIcon sx={{ fontSize: 18 }} />,
        content: <ApplicationPanel prefs={prefs} onChange={updatePref} />,
      },
      {
        id: 'hotkeys',
        label: 'Hot Keys',
        icon: <KeyboardIcon sx={{ fontSize: 18 }} />,
        content: <HotKeysPanel />,
      },
    ],
    [prefs, updatePref, user],
  );

  const handleSave = useCallback(async () => {
    await save();
    // Invalidate detail panel field preference cache so it picks up changes immediately
    queryClient.invalidateQueries({ queryKey: ['userPrefs', 'detailPanelFields'] });
    onClose();
  }, [save, onClose, queryClient]);

  return (
    <WizardDialog
      open={open}
      onClose={onClose}
      title="User Preferences"
      panels={panels}
      width={850}
      height={620}
      onSave={handleSave}
      saving={saving}
    />
  );
};

/* ── Panel section header ── */
const SectionHeader = ({ title }: { title: string }) => (
  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: gwtStyles.accentPrimary }}>
    {title}
  </Typography>
);

/* ── Field row ── */
const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 140, textAlign: 'right', flexShrink: 0 }}>
      {label}:
    </Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

type PrefPanelProps = {
  prefs: UserPreferences;
  onChange: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
};

/* ── 1. Contact Panel ── */
const ContactPanel = ({ prefs, onChange }: PrefPanelProps) => (
  <Box>
    <SectionHeader title="Personal Contact Information" />
    <FieldRow label="Phone"><TextField size="small" fullWidth value={prefs.phone ?? ''} onChange={(e) => onChange('phone', e.target.value)} sx={{ '& input': { fontSize: 12 } }} /></FieldRow>
    <FieldRow label="Email"><TextField size="small" fullWidth value={prefs.email ?? ''} onChange={(e) => onChange('email', e.target.value)} sx={{ '& input': { fontSize: 12 } }} /></FieldRow>
    <FieldRow label="Pager"><TextField size="small" fullWidth value={prefs.pager ?? ''} onChange={(e) => onChange('pager', e.target.value)} sx={{ '& input': { fontSize: 12 } }} /></FieldRow>
    <FieldRow label="Fax"><TextField size="small" fullWidth value={prefs.fax ?? ''} onChange={(e) => onChange('fax', e.target.value)} sx={{ '& input': { fontSize: 12 } }} /></FieldRow>
    <FieldRow label="Location"><TextField size="small" fullWidth value={prefs.location ?? ''} onChange={(e) => onChange('location', e.target.value)} sx={{ '& input': { fontSize: 12 } }} /></FieldRow>
  </Box>
);

/* ── 2. Notifications Panel ── */
const NotificationsPanel = ({ prefs, onChange }: PrefPanelProps) => (
  <Box>
    <SectionHeader title="Notification Configuration" />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.emailNotifications ?? true} onChange={(e) => onChange('emailNotifications', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Enable email notifications</Typography>}
    />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.browserNotifications ?? true} onChange={(e) => onChange('browserNotifications', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Enable browser notifications</Typography>}
    />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.urgentFindings ?? false} onChange={(e) => onChange('urgentFindings', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Notify on urgent findings</Typography>}
    />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.newAssignments ?? false} onChange={(e) => onChange('newAssignments', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Notify on new assignments</Typography>}
    />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.soundAlerts ?? false} onChange={(e) => onChange('soundAlerts', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Sound alerts</Typography>}
    />
  </Box>
);

/* ── 3. DICOM Panel ── */
const DicomPanel = ({ dicomAe: initialAe, dicomHost: initialHost, dicomPort: initialPort }: { dicomAe?: string; dicomHost?: string; dicomPort?: number }) => {
  const [ae, setAe] = useState(initialAe ?? '');
  const [host, setHost] = useState(initialHost ?? '');
  const [port, setPort] = useState(String(initialPort ?? ''));
  const [saving, setSaving] = useState(false);

  const aeError = ae.length > 16 ? 'AE Title must be 16 characters or fewer (DICOM standard)' : '';
  const portNum = parseInt(port, 10);
  const portError = port !== '' && (isNaN(portNum) || portNum < 1 || portNum > 65535) ? 'Port must be 1–65535' : '';

  const handleSave = useCallback(async () => {
    if (aeError || portError) return;
    setSaving(true);
    try {
      if (ae !== (initialAe ?? '')) await api.setUserAttribute('dicomAe', ae);
      if (host !== (initialHost ?? '')) await api.setUserAttribute('dicomHost', host);
      if (port !== String(initialPort ?? '')) await api.setUserAttribute('dicomPort', port);
      pushSuccess('DICOM settings updated');
    } catch (e) {
      pushError(`Failed to save DICOM settings: ${e instanceof Error ? e.message : String(e)}`);
    }
    setSaving(false);
  }, [ae, host, port, aeError, portError, initialAe, initialHost, initialPort]);

  return (
    <Box>
      <SectionHeader title="DICOM Information" />
      <FieldRow label="AE Title">
        <TextField
          size="small" fullWidth value={ae}
          onChange={(e) => setAe(e.target.value.toUpperCase())}
          error={!!aeError} helperText={aeError || undefined}
          inputProps={{ maxLength: 16 }}
          sx={{ '& input': { fontSize: 12 } }}
          placeholder="Up to 16 chars, e.g. MYSTATION"
        />
      </FieldRow>
      <FieldRow label="Host">
        <TextField size="small" fullWidth value={host} onChange={(e) => setHost(e.target.value)}
          sx={{ '& input': { fontSize: 12 } }} placeholder='Hostname or IP (use "auto" for auto-detect)' />
      </FieldRow>
      <FieldRow label="Port">
        <TextField size="small" fullWidth type="number" value={port} onChange={(e) => setPort(e.target.value)}
          error={!!portError} helperText={portError || undefined}
          sx={{ '& input': { fontSize: 12 } }} />
      </FieldRow>
      <FieldRow label="WADO URL">
        <TextField size="small" fullWidth disabled value={host ? `http://${host}:${port || 9080}/wado` : ''}
          sx={{ '& input': { fontSize: 12, color: 'text.secondary' } }} />
      </FieldRow>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          size="small" variant="contained" onClick={handleSave}
          disabled={saving || !!aeError || !!portError}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {saving ? 'Saving…' : 'Update DICOM Settings'}
        </Button>
      </Box>
    </Box>
  );
};

/* ── 4. Details Layout Panel ── */
const DETAIL_FIELDS = [
  { id: 'patientName', label: 'Patient Name' },
  { id: 'date', label: 'Date' },
  { id: 'history', label: 'History' },
  { id: 'patientId', label: 'Patient ID' },
  { id: 'status', label: 'Status' },
  { id: 'reason', label: 'Reason for Exam' },
  { id: 'accession', label: 'Accession #' },
  { id: 'urgentAction', label: 'Urgent Action' },
  { id: 'urgentReason', label: 'Urgent Reason' },
  { id: 'finalRead', label: 'Final Read' },
  { id: 'physician', label: 'Physician' },
  { id: 'contactLocation', label: 'Contact Location' },
];
const DEFAULT_DETAIL_FIELDS = DETAIL_FIELDS.map((f) => f.id);

const DetailsLayoutPanel = ({ prefs, onChange }: PrefPanelProps) => {
  const orderedFields: string[] = prefs.detailPanelFields ?? DEFAULT_DETAIL_FIELDS;

  // Ensure all known fields are represented (new fields get appended)
  const fullList = useMemo(() => {
    const result = [...orderedFields];
    for (const f of DETAIL_FIELDS) {
      if (!result.includes(f.id) && !result.includes(`-${f.id}`)) {
        result.push(f.id);
      }
    }
    return result;
  }, [orderedFields]);

  const isVisible = (fieldId: string) => !fieldId.startsWith('-');
  const rawId = (fieldId: string) => fieldId.replace(/^-/, '');
  const labelFor = (fieldId: string) =>
    DETAIL_FIELDS.find((f) => f.id === rawId(fieldId))?.label ?? rawId(fieldId);

  const handleToggle = (idx: number) => {
    const updated = [...fullList];
    const id = rawId(updated[idx]);
    updated[idx] = isVisible(updated[idx]) ? `-${id}` : id;
    onChange('detailPanelFields', updated);
  };

  const handleMove = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fullList.length) return;
    const updated = [...fullList];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    onChange('detailPanelFields', updated);
  };

  const handleReset = () => {
    onChange('detailPanelFields', DEFAULT_DETAIL_FIELDS);
  };

  const visibleCount = fullList.filter(isVisible).length;

  return (
    <Box>
      <SectionHeader title="Details Layout" />
      <Typography variant="body2" sx={{ fontSize: 12, mb: 0.5 }}>
        Choose which fields appear in the detail panel and their display order.
        The panel uses a 3-column grid — fields fill left-to-right, top-to-bottom.
      </Typography>
      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', mb: 1, display: 'block' }}>
        {visibleCount} of {DETAIL_FIELDS.length} fields visible
        ({Math.ceil(visibleCount / 3)} rows × 3 columns)
      </Typography>
      <Box sx={{ maxHeight: 340, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        {fullList.map((fieldId, idx) => (
          <Box
            key={rawId(fieldId)}
            sx={{
              display: 'flex', alignItems: 'center', px: 1, py: 0.25,
              borderBottom: idx < fullList.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              opacity: isVisible(fieldId) ? 1 : 0.5,
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            <Checkbox
              size="small" checked={isVisible(fieldId)} onChange={() => handleToggle(idx)}
              sx={{ p: 0.25, mr: 0.5 }}
            />
            <Typography variant="body2" sx={{ fontSize: 12, flex: 1 }}>
              {labelFor(fieldId)}
            </Typography>
            <IconButton size="small" onClick={() => handleMove(idx, -1)} disabled={idx === 0}
              sx={{ width: 22, height: 22 }}>
              <UpIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton size="small" onClick={() => handleMove(idx, 1)} disabled={idx === fullList.length - 1}
              sx={{ width: 22, height: 22 }}>
              <DownIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Box sx={{ mt: 1 }}>
        <Button size="small" onClick={handleReset} sx={{ textTransform: 'none', fontSize: 11 }}>
          Reset to Default
        </Button>
      </Box>
    </Box>
  );
};

/* ── 5. Columns Layout Panel ── */
const ALL_COLUMNS = [
  { id: 'name', label: 'Patient Name' },
  { id: 'id', label: 'Patient ID' },
  { id: 'sex', label: 'Sex' },
  { id: 'dob', label: 'DOB' },
  { id: 'status', label: 'Status' },
  { id: 'acc', label: 'Accession #' },
  { id: 'mod', label: 'Modality' },
  { id: 'bod', label: 'Body Part' },
  { id: 'date', label: 'Date' },
  { id: 'physician', label: 'Physician' },
  { id: 'ae', label: 'AE Title' },
  { id: 'descr', label: 'Description' },
  { id: 'radiologist', label: 'Radiologist' },
  { id: 'comment', label: 'Comment' },
  { id: 'dicomCount', label: 'DICOM Count' },
  { id: 'faxCount', label: 'Fax Count' },
  { id: 'reasonForExam', label: 'Reason for Exam' },
  { id: 'site', label: 'Site' },
  { id: 'finalRead', label: 'Final Read' },
];

const DEFAULT_VISIBLE = ['name', 'id', 'sex', 'dob', 'status', 'acc', 'mod', 'bod', 'date', 'physician', 'ae', 'descr', 'radiologist', 'comment', 'dicomCount', 'faxCount'];

const ColumnsLayoutPanel = ({ prefs, onChange }: PrefPanelProps) => {
  const visibleCols: string[] = prefs.visibleColumns ?? DEFAULT_VISIBLE;

  const handleToggle = (colId: string) => {
    const updated = visibleCols.includes(colId)
      ? visibleCols.filter((c) => c !== colId)
      : [...visibleCols, colId];
    onChange('visibleColumns', updated);
  };

  return (
    <Box>
      <SectionHeader title="Column Configuration" />
      <Typography variant="body2" sx={{ fontSize: 12, mb: 1 }}>
        Select which columns to display in the worklist grid.
      </Typography>
      <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
        {ALL_COLUMNS.map((col) => (
          <FormControlLabel
            key={col.id}
            control={<Checkbox size="small" checked={visibleCols.includes(col.id)} onChange={() => handleToggle(col.id)} />}
            label={<Typography variant="body2" sx={{ fontSize: 12 }}>{col.label}</Typography>}
            sx={{ display: 'block', m: 0, height: 26 }}
          />
        ))}
      </Box>
    </Box>
  );
};

/* ── 6. Tabs Panel ── */
const ALL_TAB_DEFS = [
  { id: 'studies', label: 'Studies' },
  { id: 'newstudy', label: 'New Studies' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'dictated', label: 'Dictated' },
  { id: 'read', label: 'Read' },
  { id: 'urgent', label: 'Urgent' },
  { id: 'onhold', label: 'On Hold' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'inprogress', label: 'In Progress' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'transcribed', label: 'Transcribed' },
  { id: 'ordered', label: 'Ordered' },
  { id: 'scheduled', label: 'Scheduled' },
];

const DEFAULT_TABS_PREF = ALL_TAB_DEFS.map((t) => t.id);

const TabsPanel = ({ prefs, onChange }: PrefPanelProps) => {
  const visibleTabs: string[] = prefs.visibleTabs ?? DEFAULT_TABS_PREF;

  const handleToggle = (tabId: string) => {
    const updated = visibleTabs.includes(tabId)
      ? visibleTabs.filter((t) => t !== tabId)
      : [...visibleTabs, tabId];
    onChange('visibleTabs', updated);
  };

  return (
    <Box>
      <SectionHeader title="Tab Configuration" />
      <Typography variant="body2" sx={{ fontSize: 12, mb: 1 }}>
        Select which default tabs appear in the worklist. Additional tabs can always be
        opened from the &ldquo;+&rdquo; menu.
      </Typography>
      <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
        {ALL_TAB_DEFS.map((tab) => (
          <FormControlLabel
            key={tab.id}
            control={<Checkbox size="small" checked={visibleTabs.includes(tab.id)} onChange={() => handleToggle(tab.id)} />}
            label={<Typography variant="body2" sx={{ fontSize: 12 }}>{tab.label}</Typography>}
            sx={{ display: 'block', m: 0, height: 26 }}
          />
        ))}
      </Box>
    </Box>
  );
};

/* ── 7. Monitor Panel ── */
const MonitorPanel = ({ prefs, onChange }: PrefPanelProps) => (
  <Box>
    <SectionHeader title="Monitor Configuration" />
    <FieldRow label="Layout">
      <Select size="small" value={prefs.monitorLayout ?? 'single'} onChange={(e) => onChange('monitorLayout', e.target.value)} fullWidth sx={{ fontSize: 12 }}>
        <MenuItem value="single" sx={{ fontSize: 12 }}>Single Monitor</MenuItem>
        <MenuItem value="dual" sx={{ fontSize: 12 }}>Dual Monitor</MenuItem>
        <MenuItem value="triple" sx={{ fontSize: 12 }}>Triple Monitor</MenuItem>
      </Select>
    </FieldRow>
    <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary', mt: 1, display: 'block' }}>
      Multi-monitor support allows the viewer to span additional screens.
    </Typography>
  </Box>
);

/* ── 8. Application Panel ── */
const ApplicationPanel = ({ prefs, onChange }: PrefPanelProps) => (
  <Box>
    <SectionHeader title="Application Settings" />
    <FieldRow label="Theme">
      <Select size="small" value={prefs.theme ?? 'blue'} onChange={(e) => onChange('theme', e.target.value)} fullWidth sx={{ fontSize: 12 }}>
        <MenuItem value="blue" sx={{ fontSize: 12 }}>Blue</MenuItem>
        <MenuItem value="amber" sx={{ fontSize: 12 }}>Amber</MenuItem>
        <MenuItem value="gray" sx={{ fontSize: 12 }}>Gray</MenuItem>
        <MenuItem value="slate" sx={{ fontSize: 12 }}>Slate</MenuItem>
        <MenuItem value="reading" sx={{ fontSize: 12 }}>Reading</MenuItem>
      </Select>
    </FieldRow>
    <FieldRow label="Page Size">
      <Select size="small" value={prefs.pageSize ?? '100'} onChange={(e) => onChange('pageSize', e.target.value)} fullWidth sx={{ fontSize: 12 }}>
        <MenuItem value="50" sx={{ fontSize: 12 }}>50</MenuItem>
        <MenuItem value="100" sx={{ fontSize: 12 }}>100</MenuItem>
        <MenuItem value="200" sx={{ fontSize: 12 }}>200</MenuItem>
        <MenuItem value="500" sx={{ fontSize: 12 }}>500</MenuItem>
      </Select>
    </FieldRow>
    <Divider sx={{ my: 1.5 }} />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.autoRefresh ?? true} onChange={(e) => onChange('autoRefresh', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Auto-refresh worklist</Typography>}
    />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.showDetailPanel ?? true} onChange={(e) => onChange('showDetailPanel', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Show detail panel</Typography>}
    />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.showAggregation ?? true} onChange={(e) => onChange('showAggregation', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Show aggregation panel</Typography>}
    />
    <FormControlLabel
      control={<Checkbox size="small" checked={prefs.compactMode ?? false} onChange={(e) => onChange('compactMode', e.target.checked)} />}
      label={<Typography variant="body2" sx={{ fontSize: 12 }}>Compact mode</Typography>}
    />
  </Box>
);

/* ── 9. Hot Keys Panel ── */
const HotKeysPanel = () => {
  const [bindings, setBindings] = useState<HotKeyBindings>(() => loadUserBindings());
  const [capturingId, setCapturingId] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const captureRef = useRef<HTMLDivElement>(null);

  // Capture keypress when in capture mode
  useEffect(() => {
    if (!capturingId) return;

    const handler = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Ignore lone modifier keys
      if (['Control', 'Alt', 'Shift', 'Meta'].includes(e.key)) return;

      const combo: KeysCombination = {
        key: e.key,
        ...(e.ctrlKey && { ctrl: true }),
        ...(e.altKey && { alt: true }),
        ...(e.shiftKey && { shift: true }),
        ...(e.metaKey && { meta: true }),
      };

      // Check for conflicts with other bindings
      const comboStr = formatKeyCombination(combo);
      for (const [actionId, existing] of bindings) {
        if (actionId !== capturingId && formatKeyCombination(existing) === comboStr) {
          const conflictAction = HOT_KEY_ACTIONS.find((a) => a.id === actionId);
          setConflict(`Already assigned to: ${conflictAction?.label ?? actionId}`);
          setTimeout(() => setConflict(null), 2500);
          return;
        }
      }

      const updated = new Map(bindings);
      updated.set(capturingId, combo);
      setBindings(updated);
      saveUserBindings(updated);
      setCapturingId(null);
      setConflict(null);
    };

    // Escape to cancel capture
    const cancelHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setCapturingId(null);
        setConflict(null);
      }
    };

    // Use capture phase to intercept before other handlers
    document.addEventListener('keydown', cancelHandler, true);
    document.addEventListener('keydown', handler, true);
    return () => {
      document.removeEventListener('keydown', cancelHandler, true);
      document.removeEventListener('keydown', handler, true);
    };
  }, [capturingId, bindings]);

  const handleBindingClick = useCallback((actionId: string) => {
    setCapturingId(actionId);
    setConflict(null);
  }, []);

  const handleClearBinding = useCallback((actionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = new Map(bindings);
    updated.delete(actionId);
    setBindings(updated);
    saveUserBindings(updated);
  }, [bindings]);

  const handleResetDefaults = useCallback(() => {
    const defaults = getDefaultBindings();
    setBindings(defaults);
    saveUserBindings(defaults);
    setCapturingId(null);
  }, []);

  const categories: { id: HotKeyCategory; label: string }[] = [
    { id: 'worklist', label: 'Worklist' },
    { id: 'viewer', label: 'Viewer' },
    { id: 'navigation', label: 'Navigation' },
    { id: 'window_level', label: 'Window/Level' },
    { id: 'annotations', label: 'Annotations' },
    { id: 'snapshots', label: 'Snapshots' },
    { id: '3d', label: '3D Viewer' },
  ];

  return (
    <Box ref={captureRef}>
      <SectionHeader title="Keyboard Shortcuts" />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="body2" sx={{ fontSize: 12, flex: 1 }}>
          Click a shortcut to rebind. Press Escape to cancel. Right-click to clear.
        </Typography>
        <Button size="small" variant="outlined" onClick={handleResetDefaults} sx={{ fontSize: 11, textTransform: 'none', py: 0 }}>
          Reset Defaults
        </Button>
      </Box>
      {conflict && (
        <Typography variant="body2" sx={{ fontSize: 11, color: 'error.main', mb: 0.5 }}>
          {conflict}
        </Typography>
      )}
      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        {categories.map((cat) => {
          const actions = HOT_KEY_ACTIONS.filter((a) => a.category === cat.id);
          if (actions.length === 0) return null;
          return (
            <Box key={cat.id} sx={{ mb: 1.5 }}>
              <Typography
                variant="body2"
                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}
              >
                {cat.label}
              </Typography>
              {actions.map((action) => {
                const combo = bindings.get(action.id);
                const isCapturing = capturingId === action.id;
                return (
                  <Box
                    key={action.id}
                    onClick={() => handleBindingClick(action.id)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      handleClearBinding(action.id, e);
                    }}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.25,
                      px: 1,
                      cursor: 'pointer',
                      borderRadius: 0.5,
                      backgroundColor: isCapturing ? 'action.selected' : 'transparent',
                      '&:hover': { backgroundColor: isCapturing ? 'action.selected' : 'action.hover' },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: 12 }}>
                      {action.label}
                    </Typography>
                    {isCapturing ? (
                      <Chip
                        label="Press a key..."
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: 10, height: 20, animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: 11,
                          color: combo ? 'primary.main' : 'text.disabled',
                          fontFamily: 'monospace',
                        }}
                      >
                        {combo ? formatKeyCombination(combo) : '—'}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
